import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, Button, Input } from "antd";
import { decideDevice, getDeviceRequest, normalizeUserCode } from "../../api/device";
import { BrandMark } from "../../ui/BrandMark";
import "./device.css";

/** /device：AI 申请接入时发来的链接。主人登录后在这里核对申请方和范围，点同意或拒绝。 */
export function DevicePage() {
  const [params, setParams] = useSearchParams();
  const code = normalizeUserCode(params.get("code") ?? "");
  return (
    <main className="device">
      <section className="device-card" aria-labelledby="device-title">
        <BrandMark />
        {code ? <Review code={code} /> : <EnterCode onCode={(c) => setParams({ code: c }, { replace: true })} />}
      </section>
    </main>
  );
}

function EnterCode({ onCode }: { onCode: (code: string) => void }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const c = normalizeUserCode(value);
    if (!c) return setError("授权码是 8 位字母和数字，形如 ABCD-EFGH");
    onCode(c);
  };
  return (
    <form onSubmit={submit} className="device-body">
      <h1 id="device-title">接入你的 AI</h1>
      <p className="device-lead">输入 AI 给你的授权码。</p>
      <Input aria-label="授权码" size="large" value={value} placeholder="ABCD-EFGH" autoFocus onChange={(e) => setValue(e.target.value)} />
      {error && <Alert type="error" showIcon message={error} />}
      <Button type="primary" size="large" htmlType="submit" block>
        下一步
      </Button>
    </form>
  );
}

function Review({ code }: { code: string }) {
  const q = useQuery({ queryKey: ["device", code], queryFn: () => getDeviceRequest(code), retry: false });
  const decide = useMutation({ mutationFn: (approve: boolean) => decideDevice(code, approve) });

  if (decide.data)
    return (
      <div className="device-body" role="status">
        <h1 id="device-title">{decide.data.status === "approved" ? "已同意" : "已拒绝"}</h1>
        <p className="device-lead">
          {decide.data.status === "approved"
            ? `${decide.data.clientName} 马上就能接入。可以回到 AI 那边了；以后在「设置 → 我的 AI」里可以随时吊销。`
            : `${decide.data.clientName} 不会拿到你的授权。`}
        </p>
      </div>
    );
  if (q.isPending) return <div className="device-body"><h1 id="device-title">正在读取申请…</h1></div>;
  if (q.isError)
    return (
      <div className="device-body">
        <h1 id="device-title">这个申请不能处理</h1>
        <Alert type="warning" showIcon message={q.error instanceof Error ? q.error.message : "读取失败"} />
        <p className="device-lead">请让 AI 重新申请，再打开它发来的新链接。</p>
      </div>
    );
  const r = q.data;
  const minutes = Math.max(1, Math.ceil(r.secondsLeft / 60));
  return (
    <div className="device-body">
      <h1 id="device-title">
        <strong>{r.clientName}</strong> 申请接入你的中台账号
      </h1>
      <Alert
        type="warning"
        showIcon
        message="只在你刚让自己的 AI 申请接入时才点同意"
        description={`这个申请发起于 ${requestedText(r.requestedAt)}。如果不是你刚让 AI 发起的，或链接是别人发给你的，请点拒绝。`}
      />
      <p className="device-lead">
        授权码 <code className="device-code">{r.userCode}</code>，请核对和 AI 那边显示的一致。同意后它能：
      </p>
      <ul className="device-scopes">
        {r.scopes.map((s) => (
          <li key={s.scope}>{s.text}</li>
        ))}
      </ul>
      <p className="device-note">
        它以「你的 AI」身份操作，看不到你看不到的东西。会新建一把「{r.keyName}」，{r.keyDays} 天后过期，可随时在「设置 → 我的 AI」里吊销。这个申请 {minutes} 分钟内有效。
      </p>
      {decide.isError && <Alert type="error" showIcon message={decide.error instanceof Error ? decide.error.message : "没能提交"} />}
      <div className="device-actions">
        <Button size="large" autoInsertSpace={false} onClick={() => decide.mutate(false)} disabled={decide.isPending}>
          拒绝
        </Button>
        <Button type="primary" size="large" autoInsertSpace={false} onClick={() => decide.mutate(true)} loading={decide.isPending && decide.variables === true} disabled={decide.isPending}>
          同意
        </Button>
      </div>
    </div>
  );
}

function requestedText(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "刚才";
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  const hm = d.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", hour12: false });
  return mins < 1 ? `${hm}（刚刚）` : `${hm}（${mins} 分钟前）`;
}
